export function base64ToBlob(base64, contentType = "", sliceSize = 512) {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (
        let offset = 0;
        offset < byteCharacters.length;
        offset += sliceSize
    ) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, { type: contentType });
}

import { Filesystem, Directory } from '@capacitor/filesystem';

export async function saveImageToAppStorage(base64Image: string, fileName: string) {
    try {
        const result = await Filesystem.writeFile({
            path: `images/${fileName}`,      // Puedes organizar en subcarpetas
            data: base64Image,               // El base64 de la imagen
            directory: Directory.Data,       // Almacenamiento privado de la app
            recursive: true
        });
        console.log("✅ Image saved:", result.uri);
        return result.uri;  // Devuelve el URI de la imagen guardada
    } catch (e) {
        console.error("❌ Error saving image", e);
        return null;  // O maneja el error como prefieras
        // throw e;
    }
}

export async function getImageLinkSE(imagePath: string): Promise<string> {
    if (isAssetPath(imagePath)) {
        // Es un asset empaquetado en el bundle, no hay que comprobar
        // console.log("✅ Using app asset:", imagePath);
        return imagePath;
    }

    if (isRemoteUrl(imagePath)) {
        const exists = await checkRemoteImage(imagePath);
        return exists ? imagePath : "/assets/img/cards/notImage.svg";
    }

    if (imagePath.startsWith("file://")) {
        const relativePath = extractRelativePath(imagePath);
        return await tryReadAndConvert(relativePath);
    }

    return await tryReadAndConvert(imagePath);
}

function isAssetPath(path: string): boolean {
    return path.startsWith("/assets/");
}

function isRemoteUrl(pathOrUrl: string): boolean {
    return /^https?:\/\//.test(pathOrUrl);
}

function extractRelativePath(uri: string): string {
    const androidMatch = uri.match(/\/files\/(.+)/);
    if (androidMatch) {
        return androidMatch[1];
    }

    const iosMatch = uri.match(/Application%20Support\/(.+)/);
    if (iosMatch) {
        return iosMatch[1];
    }

    const match = uri.match(/Documents\/(.+)/);
    return match ? match[1] : uri;
}

async function tryReadAndConvert(path: string): Promise<string> {
    try {
        const file = await Filesystem.readFile({
            path,
            directory: Directory.Data
        });
        return `data:image/jpeg;base64,${file.data}`;
    } catch (e) {
        // console.warn("❌ Could not read local file as base64:", e);
        return "/assets/img/cards/notImage.svg";
    }
}

async function checkRemoteImage(url: string): Promise<boolean> {
    try {
        const res = await fetch(url, { method: "HEAD" });
        return res.ok;
    } catch (e) {
        // console.warn(`❌ Error checking remote image: ${e}`);
        return false;
    }
}

export async function listAppImages(): Promise<{ name: string; size: number }[]> {
    try {
        const files = await Filesystem.readdir({
            path: 'images',
            directory: Directory.Data,
        });

        const imageInfoPromises = files.files.map(async (file) => {
            try {
                const stat = await Filesystem.stat({
                    path: `images/${file.name}`,
                    directory: Directory.Data,
                });

                return {
                    name: file.name,
                    size: stat.size || 0,  // en bytes
                };
            } catch (e) {
                console.warn(`❌ Could not stat file ${file.name}:`, e);
                return null;
            }
        });

        const imageInfos = await Promise.all(imageInfoPromises);
        return imageInfos.filter((info): info is { name: string; size: number } => info !== null);
    } catch (e) {
        console.error('❌ Error reading images directory:', e);
        return [];
    }
}

export async function clearAppImages() {
    try {
        // Lista el contenido del directorio de imágenes
        const dirContents = await Filesystem.readdir({
            path: 'images',
            directory: Directory.Data
        });

        if (dirContents.files.length === 0) {
            console.log("✅ No images to delete.");
            return;
        }

        // Elimina cada archivo
        for (const file of dirContents.files) {
            try {
                await Filesystem.deleteFile({
                    path: `images/${file.name}`,
                    directory: Directory.Data
                });
                console.log(`✅ Deleted image: ${file.name}`);
            } catch (deleteErr) {
                console.warn(`❌ Failed to delete image: ${file.name}`, deleteErr);
            }
        }

        console.log("✅ All images cleaned up successfully.");
    } catch (e) {
        console.error("❌ Failed to clean app images", e);
    }
}

export function resizeBase64Img(base64: string, maxSize = 1024): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            let width = img.width;
            let height = img.height;

            // Mantener proporciones, escalando al maxSize
            if (width > height) {
                if (width > maxSize) {
                    height *= maxSize / width;
                    width = maxSize;
                }
            } else {
                if (height > maxSize) {
                    width *= maxSize / height;
                    height = maxSize;
                }
            }

            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext("2d");
            if (!ctx) return reject("No 2D context");

            ctx.drawImage(img, 0, 0, width, height);
            const resizedBase64 = canvas.toDataURL("image/jpeg", 0.85); // calidad al 85%
            resolve(resizedBase64);
        };
        img.onerror = (err) => reject(err);
        img.src = base64;
    });
}